import React, { useContext } from "react";
import { rightChevron, plusCircle } from "../../CalculatorTool/Icons";
import { CalculatorStepsContext } from "../../Store/CalculatorStepsContext";
import { CalculatorSteps } from "../../../model/CalculatorSteps";
import { maxStrategyCount, wcmUrls } from "../../../shared/constants";
import { SelectStrategyType } from "./SelectStrategyType";
import {
  AllocatedStrategy,
  InvestmentStrategy,
  InvestmentStrategyType,
} from "../../../model/CalculatorInputs";
import { none, fold } from "fp-ts/lib/Option";
import {
  VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_1,
  VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_2,
  VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
  VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
  VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
  VALIDATION_MUST_SELECT_STRATEGY_TYPES,
  getFirstValidationError,
} from "../../../model/CalculatorInputsValidation";
import { ValidationMessage } from "../ValidationMessage";
import { pipe } from "fp-ts/lib/pipeable";

import "../InputSteps.scss";
import { TooltipComponent } from "../../../utils/TooltipComponent/TooltipComponent";
import {
  ANALYTICS_SCREENS,
  analyticsScreenChangeEvent,
} from "../../../utils/Analytics";

const strategyTypeValidationMessages = new Map<string, string>([
  [
    VALIDATION_MUST_SELECT_STRATEGY_TYPES,
    "Must select a strategy for all rows",
  ],
]);

export const Step2SelectStrategies = () => {
  const { steps, setSteps } = useContext(CalculatorStepsContext);

  const inputStepClasses = ["input-step", "input-step-2"];
  if (steps.stepIndex === CalculatorSteps.STEP_2_SELECT_STRATEGY_TYPES) {
    inputStepClasses.push("current-step");
  }

  const nextIcon = rightChevron("1x", "white");
  const pruBlue = "#07639d";
  const addIcon = plusCircle("1x", pruBlue);

  const handleNext = () => {
    const newIndex = steps.stepIndex + 1;
    const stepsWithVal = steps.validatedAtCurrentStep(steps.inputs);
    if (stepsWithVal.validation.isSuccess()) {
      setSteps(stepsWithVal.withStepIndex(newIndex).withRunValidationsOff());
      if (newIndex < ANALYTICS_SCREENS.length) {
        analyticsScreenChangeEvent(ANALYTICS_SCREENS[newIndex]);
      }
    } else {
      const firstError = getFirstValidationError(stepsWithVal.validation);
      pipe(
        firstError,
        fold(
          () => {
            setSteps(stepsWithVal.withRunValidationsOn());
          },
          (validationError) => {
            const stepsWithFirstError = stepsWithVal
              .withFirstValidationError(validationError)
              .withRunValidationsOn();
            setSteps(stepsWithFirstError);
          }
        )
      );
    }
  };

  const strategyTypeSelectId = "rila-calculator-tool-strategytype";
  const strategyTypeSelectLabelId = strategyTypeSelectId + "-label";

  const strategyCount = steps.inputs.strategyList.getStrategyCount();
  const strategyIndices = Array.from(Array(strategyCount).keys());
  const showAddButton = strategyCount < maxStrategyCount;

  const addStrategy = () => {
    if (steps.inputs.strategyList.getStrategyCount() < maxStrategyCount) {
      const emptyStrategy = new AllocatedStrategy(
        new InvestmentStrategy(none, none, none),
        none,
        none
      );
      const newInputs = steps.inputs.withStrategyList(
        steps.inputs.strategyList.addAllocatedStrategy(emptyStrategy)
      );
      if (
        steps.shouldRunValidations &&
        steps.validation.hasIssue(VALIDATION_MUST_SELECT_STRATEGY_TYPES)
      ) {
        setSteps(steps.validatedAtCurrentStep(newInputs));
      } else {
        setSteps(steps.withInputs(newInputs));
      }
    }
  };

  const issues = [
    VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_1,
    VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_2,
    VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_3,
  ];

  const duplicateIssues = [
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
    [
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
      VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    ],
  ];

  function getAvailableStrategyTypes(): Array<InvestmentStrategyType> {
    const availableStrategies = pipe(
      steps.inputs.term,
      fold(
        () => [],
        (term) =>
          steps.config.availableInvestmentStrategies.strategiesForTerm(term)
      )
    );

    const strategyTypesSet = new Set<InvestmentStrategyType>();
    for (const a of availableStrategies) {
      strategyTypesSet.add(a.strategyType);
    }
    const strategyTypes = Array.from(strategyTypesSet);
    strategyTypes.sort();
    return strategyTypes;
  }

  const strategyValidationMsgId = "rila-calculator-strategy-validation-message";

  const strategyTypes = getAvailableStrategyTypes();
  const strategyTypesByCode = new Map<string, InvestmentStrategyType>();
  for (const st of strategyTypes) {
    strategyTypesByCode.set(st.code, st);
  }

  function selectStrategyTypeByCode(index: number, code: string) {
    const strategyList = steps.inputs.strategyList;
    const allocatedStrategy = strategyList.getAllocatedStrategy(index);

    if (code === "none") {
      // un-selecting a StrategyType will unset the entire row (anything else is counter-intuitive)
      // in particular, it unsets the allocation percentage for this whole allocated strategy
      const noStrategy = allocatedStrategy.strategy
        .withoutStrategyType()
        .withoutBuffer()
        .withoutInvestmentIndex();
      const newInputs = steps.inputs.withStrategyList(
        strategyList.withAllocatedStrategyAt(
          index,
          allocatedStrategy
            .withStrategy(noStrategy)
            .withoutAllocation()
            .withoutCustomStartYear()
        )
      );
      if (steps.shouldRunValidations) {
        const validatedSteps = steps.validatedAtCurrentStep(newInputs);
        setSteps(validatedSteps);
      } else {
        setSteps(steps.withInputs(newInputs));
      }
    } else {
      const strategyType = strategyTypesByCode.get(code);
      const newStrategy = strategyType
        ? allocatedStrategy.strategy.withStrategyType(strategyType)
        : allocatedStrategy.strategy.withoutStrategyType();
      const newInputs = steps.inputs.withStrategyList(
        strategyList.withAllocatedStrategyAt(
          index,
          allocatedStrategy.withStrategy(newStrategy)
        )
      );
      const validatedSteps = steps.validatedAtCurrentStep(newInputs);
      if (!steps.validation.isSuccess()) {
        setSteps(validatedSteps);
      } else {
        setSteps(steps.withInputs(newInputs));
      }
    }
  }

  function removeStrategyAt(index: number) {
    if (index !== 0) {
      // TODO: can we actually remove at 0, so long as we always keep a single strategy??

      const newInputs = steps.inputs.withStrategyList(
        steps.inputs.strategyList.withStrategyAllocationRemovedAtIndex(index)
      );
      if (steps.shouldRunValidations) {
        setSteps(steps.validatedAtCurrentStep(newInputs));
      } else {
        setSteps(steps.withInputs(newInputs));
      }
    }
  }

  return (
    <div id="input-section-2" className={inputStepClasses.join(" ")}>
      <div className="input-fields">
        <div className="step-header strategy-types">
          <div className="label-header">
            <h3 id={strategyTypeSelectLabelId}>
              Index Crediting Strategy{" "}
              <TooltipComponent
                title="Strategy type"
                url={wcmUrls.strategyTypeTooltip}
                screenreaderText="More info on Index Crediting Strategy"
              />
            </h3>
          </div>
          <div className="description">
            <p className="instruction">
              Choose one or a combination of growth strategies.
            </p>
            <p className="note">
              Some strategies are not available with&nbsp;certain&nbsp;terms.
            </p>
          </div>
        </div>
        <div className="inputs step-2-inputs">
          {strategyIndices.map((i) => (
            <SelectStrategyType
              key={i}
              index={i}
              validationIssue={issues[i]}
              availableStrategyTypes={strategyTypes}
              validationMessageId={strategyValidationMsgId}
              strategyType={
                steps.inputs.strategyList.getAllocatedStrategy(i).strategy
                  .strategyType
              }
              selectStrategyType={(code: string) =>
                selectStrategyTypeByCode(i, code)
              }
              removeStrategy={() => removeStrategyAt(i)}
              invalid={steps.validation.hasIssue(issues[i])}
              isDuplicateStrategy={steps.validation.hasIssueIn(
                duplicateIssues[i]
              )}
            />
          ))}

          {showAddButton ? (
            <div className="add-strategy-row">
              <button
                type="button"
                className="btn btn-link"
                onClick={addStrategy}
              >
                {addIcon} Add another strategy
              </button>
            </div>
          ) : null}
        </div>
        <ValidationMessage
          id={strategyValidationMsgId}
          messagesByIssue={strategyTypeValidationMessages}
        />
      </div>
      <div className="next-button-row">
        <button type="button" className="btn btn-next" onClick={handleNext}>
          Next {nextIcon}
        </button>
      </div>
    </div>
  );
};
